import { Component, OnInit, computed, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { ErrorIssue, ErrorTrackingStats } from 'src/app/models/error-tracking.model';
import { Project } from 'src/app/models/project.model';
import { StatusPage, StatusPageComponent } from 'src/app/models/status-page.model';
import { TracingStats } from 'src/app/models/tracing.model';
import { ErrorTrackingService } from 'src/app/services/error-tracking.service';
import { StatusPagesService } from 'src/app/services/health-pages.service';
import { ProjectManagementService } from 'src/app/services/project-management.service';
import { TracingService } from 'src/app/services/tracing.service';

/** Each panel loads on its own, so one failing API doesn't blank the whole page. */
type LoadState = 'loading' | 'ready' | 'error';

/** Days of uptime history drawn per monitor; the status page itself shows all 90. */
const UPTIME_DAYS = 30;

@Component({
  selector: 'app-project-overview',
  templateUrl: './project-overview.component.html',
  styleUrls: ['./project-overview.component.scss'],
  standalone: false
})
export class ProjectOverviewPageComponent implements OnInit {

  projectId = '';

  readonly project = signal<Project | null>(null);
  readonly projectState = signal<LoadState>('loading');

  /** Null while loading or when the project has no status page. */
  readonly statusPage = signal<StatusPage | null>(null);
  readonly monitorsState = signal<LoadState>('loading');

  readonly errorStats = signal<ErrorTrackingStats | null>(null);
  readonly issues = signal<ErrorIssue[]>([]);
  readonly errorsState = signal<LoadState>('loading');

  readonly tracingStats = signal<TracingStats | null>(null);
  readonly tracingState = signal<LoadState>('loading');

  readonly monitors = computed(() => this.statusPage()?.components ?? []);
  readonly downCount = computed(() => this.monitors().filter(m => m.is_down && !m.is_in_maintenance).length);
  readonly maintenanceCount = computed(() => this.monitors().filter(m => m.is_in_maintenance).length);

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectManagementService,
    private statusPagesService: StatusPagesService,
    private errorTrackingService: ErrorTrackingService,
    private tracingService: TracingService,
  ) { }

  ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('uuid')!;
    this.loadProjectAndMonitors();
    this.loadErrors();
    this.loadTracing();
  }

  private async loadProjectAndMonitors() {
    try {
      const project = await lastValueFrom(this.projectService.getProject(this.projectId));
      this.project.set(project);
      this.projectState.set('ready');
    } catch {
      this.projectState.set('error');
      this.monitorsState.set('error');
      return;
    }

    const slug = this.project()!.status_page_slug;
    if (!slug) {
      this.monitorsState.set('ready');
      return;
    }
    try {
      this.statusPage.set(await lastValueFrom(this.statusPagesService.getHealthPage(slug)));
      this.monitorsState.set('ready');
    } catch {
      this.monitorsState.set('error');
    }
  }

  private async loadErrors() {
    try {
      const [stats, issues] = await Promise.all([
        lastValueFrom(this.errorTrackingService.getStats(this.projectId)),
        lastValueFrom(this.errorTrackingService.getIssues(this.projectId, { status: 'unresolved', sort: 'last_seen' })),
      ]);
      this.errorStats.set(stats);
      this.issues.set(issues.results.slice(0, 3));
      this.errorsState.set('ready');
    } catch {
      this.errorsState.set('error');
    }
  }

  private async loadTracing() {
    try {
      this.tracingStats.set(await lastValueFrom(this.tracingService.getStats(this.projectId)));
      this.tracingState.set('ready');
    } catch {
      this.tracingState.set('error');
    }
  }

  recentBars(monitor: StatusPageComponent) {
    return monitor.lastNinetyDaysHealthy.slice(-UPTIME_DAYS);
  }

  monitorState(monitor: StatusPageComponent): 'up' | 'down' | 'maintenance' {
    if (monitor.is_in_maintenance) return 'maintenance';
    return monitor.is_down ? 'down' : 'up';
  }

  formatDuration(ms: number): string {
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
    return `${ms.toFixed(0)} ms`;
  }

  relativeTime(value?: string): string {
    if (!value) return '';
    const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
    if (isNaN(seconds)) return '';
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ['year', 31536000], ['month', 2592000], ['week', 604800],
      ['day', 86400], ['hour', 3600], ['minute', 60],
    ];
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    for (const [unit, size] of units) {
      if (seconds >= size) return rtf.format(-Math.floor(seconds / size), unit);
    }
    return 'just now';
  }
}
